import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Dumbbell,
  Save,
  Loader2,
  Trash2,
  Play,
  CheckCircle,
  Circle,
  X,
  Image,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { ExerciseAutocomplete } from "../ui/ExerciseAutocomplete";

// Media Modal Component
const MediaModal = ({ media, onClose }) => {
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
    const lowerUrl = url.toLowerCase();
    return (
      videoExtensions.some((ext) => lowerUrl.includes(ext)) ||
      lowerUrl.includes("youtube") ||
      lowerUrl.includes("vimeo") ||
      lowerUrl.includes("video")
    );
  };

  if (!media) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-4xl w-full max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
        >
          <X className="w-8 h-8" />
        </button>
        <h3 className="text-white text-xl font-bold mb-4 text-center">
          {media.name}
        </h3>
        <div className="bg-gray-900 rounded-2xl overflow-hidden">
          {media.urls.length === 1 ? (
            isVideoUrl(media.urls[0]) ? (
              <video
                src={media.urls[0]}
                controls
                className="w-full max-h-[70vh] object-contain"
                autoPlay
              />
            ) : (
              <img
                src={media.urls[0]}
                alt={media.name}
                className="w-full max-h-[70vh] object-contain"
              />
            )
          ) : (
            <div className="grid grid-cols-2 gap-2 p-2">
              {media.urls.map((url, idx) => (
                <div
                  key={idx}
                  className="aspect-video bg-gray-800 rounded-lg overflow-hidden"
                >
                  {isVideoUrl(url) ? (
                    <video
                      src={url}
                      controls
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={url}
                      alt={`${media.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SessionTrainingSection = ({
  expanded,
  onToggle,
  sessionExercises,
  allExercises = [],
  onUpdateExercise,
  onAddExercise,
  onRemoveExercise,
  onAddNewExercise,
  onSave,
  saving = false,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Get exercise details based on exerciseId
  const getExerciseDetails = (exerciseId) => {
    return allExercises.find((ex) => ex.id === exerciseId) || null;
  };

  // Check if URL is a video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
    const lowerUrl = url.toLowerCase();
    return (
      videoExtensions.some((ext) => lowerUrl.includes(ext)) ||
      lowerUrl.includes("youtube") ||
      lowerUrl.includes("vimeo") ||
      lowerUrl.includes("video")
    );
  };

  // Open media modal
  const openMediaModal = (exerciseId) => {
    const exercise = getExerciseDetails(exerciseId);
    if (exercise?.mediaUrls && exercise.mediaUrls.length > 0) {
      setSelectedMedia({ urls: exercise.mediaUrls, name: exercise.name });
    }
  };

  const handleSelectExercise = (exerciseId, selectedExercise) => {
    // Update both ID and Name to ensure consistency.
    // If ID is null (cleared), Name should be empty.
    // If ID is valid, Name should be the exercise name (though ID takes precedence for display usually).
    onUpdateExercise(exerciseId, "exerciseId", selectedExercise.id);
    onUpdateExercise(exerciseId, "exerciseName", selectedExercise.name || "");
  };

  const handleAddNewForCard = (exerciseId, name) => {
    if (onAddNewExercise) {
      onAddNewExercise(name, exerciseId);
    }
  };

  return (
    <>
      <div
        className={`${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg mb-6 transition-all duration-300 relative z-20`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 cursor-pointer ${colors.tableRowHover} transition-colors`}
          onClick={onToggle}
        >
          <h2
            className={`text-lg font-bold ${colors.textPrimary} flex items-center`}
          >
            <Dumbbell className={`w-5 h-5 mr-2 ${colors.textSecondary}`} />
            {t("sessionDetails.training.title")}
          </h2>

          <div className="flex items-center space-x-2">
            {expanded ? (
              <ChevronUp className={`w-5 h-5 ${colors.textTertiary}`} />
            ) : (
              <ChevronDown className={`w-5 h-5 ${colors.textTertiary}`} />
            )}
          </div>
        </div>

        {/* Expanded Content */}
        {expanded && (
          <div className={`border-t ${colors.tableBorder} px-4 py-4 md:px-6`}>
            {/* List Header - Desktop Only */}
            {sessionExercises.length > 0 && (
              <div className="hidden md:grid grid-cols-12 gap-4 mb-3 px-2 text-xs font-semibold uppercase tracking-wider opacity-60">
                <div className="col-span-5 pl-2">
                  {t("sessionDetails.training.exercise")}
                </div>
                <div className="col-span-1 text-center">
                  {t("sessionDetails.training.sets")}
                </div>
                <div className="col-span-1 text-center">
                  {t("sessionDetails.training.reps")}
                </div>
                <div className="col-span-1 text-center">
                  {t("sessionDetails.training.kg")}
                </div>
                <div className="col-span-3 pl-2">
                  {t("sessionDetails.training.notes")}
                </div>
                <div className="col-span-1"></div>
              </div>
            )}

            {/* Exercises List */}
            <div className="space-y-3">
              {sessionExercises.map((exercise, index) => {
                const exerciseDetails = getExerciseDetails(exercise.exerciseId);
                const hasMedia =
                  exerciseDetails?.mediaUrls &&
                  exerciseDetails.mediaUrls.length > 0;
                const mediaUrl = hasMedia ? exerciseDetails.mediaUrls[0] : null;
                const isVideo = isVideoUrl(mediaUrl);

                return (
                  <div
                    key={exercise.id}
                    className={`relative group grid md:grid-cols-12 gap-3 md:gap-4 items-center p-3 rounded-xl border ${colors.inputBorder || "border-gray-200"} ${colors.inputBg || "bg-white"} hover:shadow-md transition-all duration-200 focus-within:z-50 focus-within:ring-1 focus-within:ring-blue-400/30`}
                  >
                    {/* Exercise Selection / Info */}
                    <div className="col-span-12 md:col-span-5 flex items-center space-x-3">
                      {/* Media Preview / Number */}
                      <div
                        className={`relative w-10 h-10 md:w-12 md:h-12 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer border ${colors.border || "border-gray-200"} bg-gray-100 dark:bg-gray-800 flex items-center justify-center group/media`}
                        onClick={() => {
                          if (hasMedia) openMediaModal(exercise.exerciseId);
                        }}
                      >
                        {hasMedia ? (
                          <>
                            {isVideo ? (
                              <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                <Play className="w-4 h-4 text-white" />
                              </div>
                            ) : (
                              <img
                                src={mediaUrl}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/media:opacity-100 transition-opacity flex items-center justify-center">
                              <Play className="w-4 h-4 text-white drop-shadow-md" />
                            </div>
                          </>
                        ) : (
                          <span className="text-xs font-bold text-gray-400">
                            {index + 1}
                          </span>
                        )}
                      </div>

                      {/* Autocomplete Input */}
                      <div className="flex-1 min-w-0">
                        <div className="md:hidden text-xs font-semibold text-gray-500 mb-1">
                          {t("sessionDetails.training.exercise")}
                        </div>
                        <ExerciseAutocomplete
                          allExercises={allExercises}
                          value={
                            exerciseDetails?.name || exercise.exerciseName || ""
                          }
                          onSelect={(selected) =>
                            handleSelectExercise(exercise.id, selected)
                          }
                          onAddNew={(name) =>
                            handleAddNewForCard(exercise.id, name)
                          }
                          placeholder={t(
                            "sessionDetails.training.searchPlaceholder",
                          )}
                          colors={colors}
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Stats Inputs (Sets, Reps, Kg) */}
                    <div className="col-span-12 md:col-span-3 grid grid-cols-3 gap-2">
                      <div>
                        <label className="md:hidden block text-[10px] uppercase font-bold text-gray-500 mb-1">
                          {t("sessionDetails.training.sets")}
                        </label>
                        <input
                          type="number"
                          value={exercise.sets || ""}
                          onChange={(e) =>
                            onUpdateExercise(
                              exercise.id,
                              "sets",
                              e.target.value,
                            )
                          }
                          placeholder="0"
                          className={`w-full px-2 py-2 md:py-1.5 text-center text-sm rounded-lg border ${colors.inputBorder} ${colors.inputBg} focus:ring-2 focus:ring-blue-500/50 outline-none`}
                        />
                      </div>
                      <div>
                        <label className="md:hidden block text-[10px] uppercase font-bold text-gray-500 mb-1">
                          {t("sessionDetails.training.reps")}
                        </label>
                        <input
                          type="number"
                          value={exercise.reps || ""}
                          onChange={(e) =>
                            onUpdateExercise(
                              exercise.id,
                              "reps",
                              e.target.value,
                            )
                          }
                          placeholder="0"
                          className={`w-full px-2 py-2 md:py-1.5 text-center text-sm rounded-lg border ${colors.inputBorder} ${colors.inputBg} focus:ring-2 focus:ring-blue-500/50 outline-none`}
                        />
                      </div>
                      <div>
                        <label className="md:hidden block text-[10px] uppercase font-bold text-gray-500 mb-1">
                          {t("sessionDetails.training.kg")}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={exercise.weight || ""}
                            onChange={(e) =>
                              onUpdateExercise(
                                exercise.id,
                                "weight",
                                e.target.value,
                              )
                            }
                            placeholder="0"
                            className={`w-full px-2 py-2 md:py-1.5 text-center text-sm rounded-lg border ${colors.inputBorder} ${colors.inputBg} focus:ring-2 focus:ring-blue-500/50 outline-none`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="col-span-11 md:col-span-3">
                      <label className="md:hidden block text-[10px] uppercase font-bold text-gray-500 mb-1">
                        {t("sessionDetails.training.notes")}
                      </label>
                      <input
                        type="text"
                        value={exercise.notes || ""}
                        onChange={(e) =>
                          onUpdateExercise(exercise.id, "notes", e.target.value)
                        }
                        placeholder={t(
                          "sessionDetails.training.addNotesPlaceholder",
                        )}
                        className={`w-full px-3 py-2 md:py-1.5 text-sm rounded-lg border ${colors.inputBorder} ${colors.inputBg} focus:ring-2 focus:ring-blue-500/50 outline-none truncate focus:text-clip`}
                      />
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 md:col-span-1 flex items-center justify-end space-x-2">
                      {/* Status Toggle */}
                      <button
                        onClick={() =>
                          onUpdateExercise(
                            exercise.id,
                            "completed",
                            !exercise.completed,
                          )
                        }
                        className={`p-1.5 rounded-lg transition-colors ${
                          exercise.completed
                            ? "text-green-500 bg-green-50 dark:bg-green-900/20"
                            : "text-gray-400 hover:text-green-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                        title={
                          exercise.completed
                            ? t("sessionDetails.training.markIncomplete")
                            : t("sessionDetails.training.markComplete")
                        }
                      >
                        {exercise.completed ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <Circle className="w-5 h-5" />
                        )}
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={() => onRemoveExercise(exercise.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title={t("sessionDetails.training.removeExercise")}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer Actions */}
            <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <button
                onClick={() => onAddExercise && onAddExercise(null)}
                className="w-full md:w-auto flex items-center justify-center space-x-2 px-5 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-blue-400 hover:text-blue-500 transition-all font-medium text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>{t("sessionDetails.training.addExercise")}</span>
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSave && onSave();
                }}
                disabled={saving || sessionExercises.length === 0}
                className={`w-full md:w-auto flex items-center justify-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t("sessionDetails.training.saving")}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{t("sessionDetails.training.saveChanges")}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Media Modal */}
      <MediaModal
        media={selectedMedia}
        onClose={() => setSelectedMedia(null)}
      />
    </>
  );
};
